/* Lille Björn — Quick Shop behaviour ("Atelier", Sep 2026)

   Layered on top of the theme; it never replaces theme code:
     · SHTDialogQuickShop / SHTDialogCore   assets/theme.js
     · component-quickview.js               variant repaint, cart submit
     · prdQckVwAndModal                     the collar/harness size check

   What it adds, for every product the dialog loads:
     1. Sold-out sync — the theme only updates the hidden submit button, so
        the visible size-check button kept saying "Add to cart" on a sold-out
        size. Its disabled state and label now follow the submit button.
     2. Refresh look — between a size tap and the refreshed price the theme
        disables the button; if it was enabled, it keeps its look (no flash).
     3. Fit — picks the roomiest layout level (data-fit 0–4) whose content
        fits the panel, and the most even size-grid split whose labels fit.
     4. Guards — a second card's Add to cart cannot start a competing fetch
        while one is loading; it is loaded as soon as the first has opened,
        so the latest tap wins and the older product is never shown.
     5. Size check — Confirm acts on the open dialog only. The theme adds a
        Confirm listener per dialog ever opened, and the stale ones throw
        (and on a collar PDP the page's own add-to-cart fired as well). It
        also leaves the page scrollable behind the dialog; focus moves into
        the check and back; Escape closes the check first; the second click
        of a double-click on Add to cart no longer dismisses it.
     8. Framing — every photo is shown as large as possible without cutting
        the product (see section 8); the studio backdrop is extended softly
        where the photo cannot reach the box edges.
     7. Stacking — the size check is lifted above the dialog while it belongs
        to it, and <html> carries .lbqs-open so the chat launcher can step aside.
     6. Stale refresh — size taps in quick succession can answer out of
        order; if the price on screen belongs to another variant than the
        one selected, the refresh is asked for again.
     9. Focus — trapped the moment the dialog shows, not when the theme's
        wait for every animation (Shop Pay's shimmer included) is over; a
        close inside that wait hands focus back to the card.
    10. Width — on a page wider than the screen the dialog spans the screen.

   Loaded by snippets/quickshop.liquid and by the section's loader; it
   initialises once. Touches nothing outside #dialogQuickShop and #sizeModal. */
(function () {
  'use strict';
  if (window.__lbQuickShop) return;
  window.__lbQuickShop = true;

  var LEVELS = 4;
  var SEL = {
    host: '#dialogQuickShop',
    view: '[data-lbqs]',
    submit: '.js-product-form-submit-btn',
    gate: 'prd-qck-vw-and-modal > button[type="button"]',
    stock: '.lbqs__stock'
  };

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  /* ── 1 + 2: button state ───────────────────────────────────────────── */
  function syncGate(view) {
    var submit = view.querySelector(SEL.submit);
    var gate = view.querySelector(SEL.gate);
    if (!submit || !gate) return;
    var off = submit.hasAttribute('disabled');
    var label = (submit.querySelector('.js-product-form-submit-btn-text') || submit).textContent.trim();
    var gateText = gate.querySelector('.lbqs__btn-text') || gate;
    if (off) gate.setAttribute('disabled', 'disabled');
    else gate.removeAttribute('disabled');
    // while a refresh is pending the theme leaves the label unchanged; copy it only when it says something
    if (label && gateText.textContent.trim() !== label) gateText.textContent = label;
  }

  function watchButtons(view) {
    var submit = view.querySelector(SEL.submit);
    if (!submit) return;
    var mo = new MutationObserver(function () { syncGate(view); });
    mo.observe(submit, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    syncGate(view);

    var stock = view.querySelector(SEL.stock);
    var timer = 0;
    var clear = function () { clearTimeout(timer); view.removeAttribute('data-refreshing'); };
    // capture phase: this runs before the theme's own change handler, so the
    // button still shows its pre-tap state here
    view.addEventListener('change', function (e) {
      if (!e.target.closest('.js-variant-radio-item, .js-variant-select-item')) return;
      var gate = view.querySelector(SEL.gate);
      var visible = gate && gate.offsetParent !== null ? gate : submit;
      if (visible.hasAttribute('disabled')) return;
      view.setAttribute('data-refreshing', '');
      clearTimeout(timer);
      timer = setTimeout(clear, 6000);   // a failed fetch never leaves it set
    }, true);
    if (stock) {
      new MutationObserver(function () {
        if (!stock.textContent.trim()) return;
        if (stale(view)) return;            // a newer answer is on its way
        clear(); schedule(view);
      }).observe(stock, { childList: true, subtree: true, characterData: true });
    }
    var error = view.querySelector('.js-product-form-error-wrapper');
    if (error) new MutationObserver(function () { schedule(view); }).observe(error, { attributes: true, childList: true, subtree: true });
  }

  /* ── 6: an out-of-order answer ─────────────────────────────────────── */
  function stale(view) {
    var shown = view.querySelector('.lbqs__price-in[data-variant]');
    var idInput = view.querySelector('form input[name="id"]');
    if (!shown || !idInput || !idInput.value || shown.getAttribute('data-variant') === idInput.value) {
      view._lbqsRetries = 0;
      return false;
    }
    if ((view._lbqsRetries = (view._lbqsRetries || 0) + 1) > 3) return false;
    var picker = view.querySelector('sht-prd-qck-vw-variant-radios, sht-prd-qck-vw-variant-selects');
    if (picker) picker.dispatchEvent(new Event('change'));
    return true;
  }

  /* ── 8: product framing ───────────────────────────────────────────────
     Cropping the studio backdrop is fine; cutting the product is not. Each
     photo is read once at 64 px wide: the backdrop of every row is taken
     from its two edges (the sweep is smooth), anything that differs from it
     is product, and the product's box (+4 %) is what must stay in view.
     The photo is then drawn as large as that allows, up to filling the box
     (never larger); where it cannot reach the box edges, the plinth shows the
     same photo blurred behind it, so the studio sweep simply continues. If a
     photo cannot be read (no CORS, not loaded) the CSS default — the whole
     photo, contained — stays. */
  var boxCache = new WeakMap();
  function detect(img) {
    if (boxCache.has(img)) return boxCache.get(img);
    var box = null;
    try {
      var W = 64, H = Math.max(8, Math.round(W * img.naturalHeight / img.naturalWidth));
      var c = document.createElement('canvas'); c.width = W; c.height = H;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, W, H);
      var d = ctx.getImageData(0, 0, W, H).data;
      var cols = new Array(W).fill(0), rows = new Array(H).fill(0), T = 54, E = 3;
      var edgeL = [], edgeR = [], edgeT = [], edgeB = [];
      for (var xx = 0; xx < W; xx++) {
        var tp = [0, 0, 0], bt = [0, 0, 0];
        for (var e2 = 0; e2 < E; e2++) for (var c2 = 0; c2 < 3; c2++) { tp[c2] += d[(e2 * W + xx) * 4 + c2] / E; bt[c2] += d[((H - 1 - e2) * W + xx) * 4 + c2] / E; }
        edgeT.push(tp); edgeB.push(bt);
      }
      for (var y = 0; y < H; y++) {
        var L = [0, 0, 0], R = [0, 0, 0];
        for (var e = 0; e < E; e++) {
          for (var ch = 0; ch < 3; ch++) { L[ch] += d[(y * W + e) * 4 + ch] / E; R[ch] += d[(y * W + W - 1 - e) * 4 + ch] / E; }
        }
        edgeL.push(L); edgeR.push(R);
        for (var x = E; x < W - E; x++) {
          var t = x / (W - 1), k = (y * W + x) * 4, diff = 0;
          for (var cc = 0; cc < 3; cc++) diff += Math.abs(d[k + cc] - (L[cc] + (R[cc] - L[cc]) * t));
          if (diff > T) { cols[x]++; rows[y]++; }
        }
      }
      var x0 = cols.findIndex(function (n) { return n >= 2; }), y0 = rows.findIndex(function (n) { return n >= 2; });
      var x1 = W - 1 - cols.slice().reverse().findIndex(function (n) { return n >= 2; });
      var y1 = H - 1 - rows.slice().reverse().findIndex(function (n) { return n >= 2; });
      if (x0 >= 0 && y0 >= 0 && x1 > x0 && y1 > y0) {
        var m = 0.04;
        box = { x0: Math.max(0, x0 / W - m), y0: Math.max(0, y0 / H - m), x1: Math.min(1, (x1 + 1) / W + m), y1: Math.min(1, (y1 + 1) / H + m),
                edges: { L: edgeL, R: edgeR, T: edgeT, B: edgeB } };
      }
    } catch (err) { box = null; }
    boxCache.set(img, box);
    return box;
  }
  window.__lbqsDetect = detect;   // test hook (preview/productbox-test.html)

  // The studio sweep, rebuilt from the photo's own edge colours: 2 × rows for a
  // gap left/right (each row runs from its left edge colour to its right one),
  // columns × 2 for a gap above/below. Stretched to the plinth it continues
  // the backdrop smoothly and cannot contain any of the product.
  var sweepCache = new WeakMap();
  function sweep(box, axis) {
    var key = sweepCache.get(box) || {};
    if (key[axis]) return key[axis];
    var e = box.edges, a = axis === 'x' ? [e.L, e.R] : [e.T, e.B], n = a[0].length;
    var c = document.createElement('canvas');
    c.width = axis === 'x' ? 2 : n; c.height = axis === 'x' ? n : 2;
    var ctx = c.getContext('2d'), img = ctx.createImageData(c.width, c.height);
    for (var i = 0; i < n; i++) for (var side = 0; side < 2; side++) {
      var px = axis === 'x' ? (i * 2 + side) : (side * n + i), col = a[side][i];
      img.data[px * 4] = col[0]; img.data[px * 4 + 1] = col[1]; img.data[px * 4 + 2] = col[2]; img.data[px * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    key[axis] = c.toDataURL('image/png');
    sweepCache.set(box, key);
    return key[axis];
  }

  function frame(view) {
    var plinth = view.querySelector('.lbqs__plinth');
    var li = view.querySelector('.lbqs__photo:not(.d-none)') || view.querySelector('.lbqs__photo');
    var img = li && li.querySelector('img');
    if (!plinth || !img) return;
    if (!img.complete || !img.naturalWidth) {
      img.addEventListener('load', function () { schedule(view); }, { once: true });
      return;
    }
    var box = detect(img);
    Array.prototype.forEach.call(view.querySelectorAll('.lbqs__photo img'), function (o) { if (o !== img) o.removeAttribute('style'); });
    var pr = plinth.getBoundingClientRect(), bl = parseFloat(getComputedStyle(plinth).borderLeftWidth) || 0, bb = parseFloat(getComputedStyle(plinth).borderRightWidth) || 0;
    var bw = plinth.clientWidth ? pr.width - bl - bb : 0, bh = plinth.clientHeight;
    if (!box || !bw || !bh) { img.removeAttribute('style'); plinth.removeAttribute('data-extend'); return; }
    var w = img.naturalWidth, h = img.naturalHeight;
    var cover = Math.max(bw / w, bh / h), contain = Math.min(bw / w, bh / h);
    var fitBox = Math.min(bw / ((box.x1 - box.x0) * w), bh / ((box.y1 - box.y0) * h));
    var s = Math.max(contain, Math.min(cover, fitBox));
    var rw = w * s, rh = h * s;
    var cx = (box.x0 + box.x1) / 2 * rw, cy = (box.y0 + box.y1) / 2 * rh;
    var left = rw >= bw ? Math.min(0, Math.max(bw - rw, bw / 2 - cx)) : (bw - rw) / 2;
    var top = rh >= bh ? Math.min(0, Math.max(bh - rh, bh / 2 - cy)) : (bh - rh) / 2;
    img.style.cssText = 'object-fit:fill;inset:auto;left:' + left.toFixed(1) + 'px;top:' + top.toFixed(1) + 'px;width:' + rw.toFixed(1) + 'px;height:' + rh.toFixed(1) + 'px;';
    var gaps = rw < bw - 1 || rh < bh - 1;
    if (gaps) {
      var axis = rw < bw - 1 ? 'x' : 'y';
      plinth.setAttribute('data-extend', axis);
      plinth.style.setProperty('--lbqs-backdrop', 'url("' + sweep(box, axis) + '")');
    } else {
      plinth.removeAttribute('data-extend');
    }
  }

  /* ── 3: fit ────────────────────────────────────────────────────────── */
  // scrollWidth is rounded to whole pixels and hides the fractional overflow
  // that already triggers an ellipsis; a Range measures the text exactly.
  var range = document.createRange();
  function clipped(el) {
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width > el.getBoundingClientRect().width + 0.01;
  }
  function textOverflows(list) {
    var spans = list.querySelectorAll('.lbqs__chip-main, .lbqs__chip-sub');
    for (var i = 0; i < spans.length; i++) {
      if (clipped(spans[i])) return true;
    }
    return false;
  }

  function layoutChips(view, level) {
    var lists = view.querySelectorAll('.lbqs__chips');
    Array.prototype.forEach.call(lists, function (list) {
      var n = list.children.length;
      if (!n) return;
      var maxCols = level >= 2 ? 8 : 5;
      var tried = {};
      for (var rows = 1; rows <= n; rows++) {
        var cols = Math.ceil(n / rows);
        if (cols > maxCols || tried[cols]) continue;
        tried[cols] = true;
        list.style.setProperty('--lbqs-cols', cols);
        if (cols === 1 || !textOverflows(list)) return;
      }
    });
  }

  function overflows(view) {
    var body = view.querySelector('.lbqs__body');
    var panel = view.closest('.lbqs-dialog__panel');
    return (body && body.scrollHeight > body.clientHeight + 1) ||
           (panel && panel.scrollHeight > panel.clientHeight + 1);
  }

  function titleLines(title) {
    var lh = parseFloat(getComputedStyle(title).lineHeight) || 1;
    return Math.round((title.getBoundingClientRect().height - parseFloat(getComputedStyle(title).paddingTop)) / lh);
  }
  // a product name longer than three lines steps down one, then two sizes
  function titleSize(view) {
    var title = view.querySelector('.lbqs__title');
    view.removeAttribute('data-title');
    if (!title || titleLines(title) <= 3) return;
    view.setAttribute('data-title', 'long');
    if (titleLines(title) > 3) view.setAttribute('data-title', 'xlong');
  }

  function applyLevel(view, level) {
    view.setAttribute('data-fit', String(level));
    titleSize(view);
    view.removeAttribute('data-buttons');
    layoutChips(view, level);
    var labels = view.querySelectorAll('.lbqs__btn-text');
    for (var i = 0; i < labels.length; i++) {
      if (labels[i].offsetParent !== null && clipped(labels[i])) { view.setAttribute('data-buttons', 'stack'); break; }
    }
  }
  function fit(view) {
    if (!view.isConnected) return;
    // the closed dialog stays laid out (only invisible), so this measures
    // before the panel animates in and no level change is ever seen
    view.removeAttribute('data-scroll');
    view.style.removeProperty('height');
    var level;
    for (level = 0; level <= LEVELS; level++) {
      applyLevel(view, level);
      if (level === LEVELS || !overflows(view)) break;
    }
    var panel = view.closest('.lbqs-dialog__panel'), plinth = view.querySelector('.lbqs__plinth');
    // Side by side, the frame takes the photo's shape: on a tall screen with a
    // narrow photo column (768×1024 gives 360×540) a long name and three size
    // rows ran out of room with half the screen empty. Before anything
    // scrolls, the frame grows — up to the panel's own cap — and the roomiest
    // level that fits the taller frame is used.
    if (level === LEVELS && overflows(view) && panel && getComputedStyle(view).display === 'grid') {
      var base = view.getBoundingClientRect().height;
      var cap = parseFloat(getComputedStyle(panel).maxHeight) || window.innerHeight;
      if (cap > base + 1) {
        for (level = 0; level <= LEVELS; level++) {
          applyLevel(view, level);
          view.style.height = 'auto';
          var need = Math.ceil(view.getBoundingClientRect().height);
          view.style.height = Math.min(Math.max(need, base), cap) + 'px';
          if (level === LEVELS || !overflows(view)) break;
        }
      }
    }
    if (level === LEVELS && overflows(view)) view.setAttribute('data-scroll', '');
    // the header starts where the photo column ends (its width follows the photo's shape)
    if (panel && plinth) {
      var split = plinth.offsetParent !== null && getComputedStyle(plinth).position !== 'absolute' && getComputedStyle(view).display === 'grid';
      if (split) panel.style.setProperty('--lbqs-head-left', plinth.getBoundingClientRect().width + 'px');
      else panel.style.removeProperty('--lbqs-head-left');
    }
    frame(view);
    view.dispatchEvent(new CustomEvent('lbqs:fit', { bubbles: true, detail: { level: level } }));
  }

  var pending = new Set();
  var raf = 0;
  function schedule(view) {
    pending.add(view);
    if (raf) return;
    raf = requestAnimationFrame(function () {
      raf = 0;
      var list = Array.from(pending);
      pending.clear();
      list.forEach(fit);
    });
  }

  /* ── setup per loaded product ──────────────────────────────────────── */
  function setup(view) {
    if (view.dataset.lbqsReady) return;
    view.dataset.lbqsReady = '1';
    var photos = view.querySelector('.lbqs__photos');
    if (photos) new MutationObserver(function () { schedule(view); }).observe(photos, { attributes: true, subtree: true, attributeFilter: ['class'] });
    watchButtons(view);
    fit(view);
    var imgs = view.querySelectorAll('img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (!img.complete) img.addEventListener('load', function () { schedule(view); }, { once: true });
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { schedule(view); });
  }

  function currentView() {
    var host = document.querySelector(SEL.host);
    return host ? host.querySelector(SEL.view) : null;
  }

  function refit() {
    var v = currentView();
    if (v) schedule(v);
  }

  ready(function () {
    var host = document.querySelector(SEL.host);
    if (!host) return;
    var body = host.querySelector('.js-dialog-body');

    if (body) {
      new MutationObserver(function () {
        var v = body.querySelector(SEL.view);
        if (v) setup(v);
        if (v && v !== swapFrom && host.hasAttribute('data-lbqs-swap')) {
          endSwap();
          // The swap happens with the dialog already open, so neither the
          // theme's trap nor ours re-ran: focus was left on the removed content.
          trapNow(0);
        }
      }).observe(body, { childList: true, subtree: false });
      var existing = body.querySelector(SEL.view);
      if (existing) setup(existing);
    }
    // opening: the body is filled while the host is still hidden, so fit again now
    var syncOpen = function () { document.documentElement.classList.toggle('lbqs-open', !host.hasAttribute('hidden')); };
    syncOpen();
    var queued = null, swapFrom = null, swapTimer = 0;
    var endSwap = function () { clearTimeout(swapTimer); swapFrom = null; host.removeAttribute('data-lbqs-swap'); };
    new MutationObserver(function () {
      syncOpen();
      if (host.hasAttribute('hidden')) { queued = null; endSwap(); return; }
      // The theme opens the dialog even when the product fetch failed (a 429
      // or error page has no quick-view section), leaving an empty panel with
      // only a close button. Take the shopper to the product page instead.
      if (body && !body.querySelector(SEL.view) && !host.hasAttribute('data-lbqs-swap')) {
        var failedFrom = host.opener;
        setTimeout(function () {
          if (host.hasAttribute('hidden') || body.querySelector(SEL.view) || host.hasAttribute('data-lbqs-swap')) return;
          var url = failedFrom && failedFrom.getAttribute('data-product-url');
          if (!url) return;
          if (typeof host.closeModal === 'function') host.closeModal();
          window.location.assign(url.split('?')[0]);
        }, 60);
      }
      // a newer card was tapped while this one loaded: load it now, with the
      // panel's content held back so the older product never shows
      if (queued) {
        var next = queued;
        queued = null;
        if (next !== host.opener && next.isConnected && typeof host.showModal === 'function') {
          swapFrom = currentView();
          host.setAttribute('data-lbqs-swap', '');
          swapTimer = setTimeout(endSwap, 8000);   // a failed fetch never leaves it blank
          host.showModal(next);
        }
      }
      pinToScreen();
      var v = currentView();
      if (v) fit(v);
      trapNow(0);
    }).observe(host, { attributes: true, attributeFilter: ['hidden'] });

    /* ── 10: a page wider than the screen ──────────────────────────── */
    // At 280px the theme's header does not fit, so the page is 306px wide and
    // a fixed box spans the page, not the screen: the dialog's right edge
    // (close button, last size column) was off screen. Pin it to what is on
    // screen — unless the shopper has zoomed in, which is theirs to pan.
    function pinToScreen() {
      var vv = window.visualViewport;
      var over = vv && !host.hasAttribute('hidden') && vv.scale < 1.01 && window.innerWidth - vv.width > 1;
      if (over) {
        host.style.setProperty('--lbqs-vvl', vv.offsetLeft + 'px');
        host.style.setProperty('--lbqs-vvr', Math.max(0, window.innerWidth - vv.width - vv.offsetLeft) + 'px');
      } else {
        host.style.removeProperty('--lbqs-vvl');
        host.style.removeProperty('--lbqs-vvr');
      }
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', pinToScreen, { passive: true });
      window.visualViewport.addEventListener('scroll', pinToScreen, { passive: true });
    }

    /* ── 9: focus from the first frame ─────────────────────────────── */
    // The theme moves focus in (SHTHelper.trapFocus) only once every animation
    // in the panel has finished — Shop Pay's loading shimmer included — so on a
    // slow connection the open dialog sat for seconds with focus on <body>:
    // Escape (a keyup on the dialog) did nothing and Tab walked the page behind.
    // Trap as soon as the dialog shows; when the theme's own trapFocus runs
    // late, focus goes back to wherever the shopper has moved it since.
    function trapNow(tries) {
      if (host.hasAttribute('hidden') || host.contains(document.activeElement)) return;
      if (window.SHTHelper && SHTHelper.trapFocus) SHTHelper.trapFocus(host);
      else host.focus({ preventScroll: true });
      // not focusable until the open state has painted — try again next frame
      if (!host.contains(document.activeElement) && tries < 10) requestAnimationFrame(function () { trapNow(tries + 1); });
    }
    host.addEventListener('opened', function () {
      // Closed before the theme's wait was over: its late trapFocus pulls focus
      // into the closing dialog, which then vanishes and leaves focus on <body>.
      // Undo it and hand focus back to the card, as the close itself did.
      if (host.hasAttribute('hidden')) {
        var opener = host.opener;
        Promise.resolve().then(function () {
          if (!host.hasAttribute('hidden')) return;
          if (window.SHTHelper && SHTHelper.removeTrapFocus) SHTHelper.removeTrapFocus();
          if (opener && opener.isConnected && (document.activeElement === host || document.activeElement === document.body)) opener.focus({ preventScroll: true });
        });
        return;
      }
      var a = document.activeElement;
      if (!a || a === host || !host.contains(a)) return;
      // runs after the theme's trapFocus(host), which follows this event synchronously
      Promise.resolve().then(function () {
        if (a.isConnected && document.activeElement === host) a.focus({ preventScroll: true });
      });
    });

    /* ── 11: Escape in the Fit Finder closes the Fit Finder only ────── */
    // The Fit Finder (opened from inside Quick Shop) consumes the Escape
    // KEYDOWN and hands focus back into this dialog — but the theme closes
    // Quick Shop on the KEYUP, which then lands here and closed it as well.
    var ffEscape = false;
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !host.hasAttribute('hidden') && document.querySelector('lillebjorn-fit-finder[data-open]')) ffEscape = true;
    }, true);
    window.addEventListener('keyup', function (e) {
      if (e.key !== 'Escape' || !ffEscape) return;
      ffEscape = false;
      if (host.contains(e.target)) e.stopPropagation();
    }, true);

    /* ── 12: Tab stays in the dialog at every fit level ──────────────── */
    // The theme's trap wraps on the LAST focusable it found at open — the fine
    // print's link, which fits 3 and 4 hide. Tab then walked out to the page
    // behind, and Shift+Tab on the first control went nowhere. Wrap on what
    // is actually visible; anything that still slips out (Shop Pay's own
    // buttons live in a shadow root) is brought back.
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';
    var visibleFocusables = function () {
      return Array.prototype.filter.call(host.querySelectorAll(FOCUSABLE), function (n) {
        return n.getClientRects().length > 0 && getComputedStyle(n).visibility !== 'hidden';
      });
    };
    var tabbing = null;
    host.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || host.hasAttribute('hidden')) return;
      var f = visibleFocusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], at = document.activeElement;
      if (e.shiftKey && (at === first || at === host)) {
        e.preventDefault(); e.stopPropagation(); last.focus();
      } else if (!e.shiftKey && at === last) {
        e.preventDefault(); e.stopPropagation(); first.focus();
      } else {
        tabbing = e.shiftKey ? 'back' : 'forward';
        setTimeout(function () { tabbing = null; }, 0);
      }
    }, true);
    document.addEventListener('focusin', function (e) {
      if (!tabbing || host.hasAttribute('hidden') || host.contains(e.target)) return;
      var f = visibleFocusables();
      if (!f.length) return;
      (tabbing === 'back' ? f[f.length - 1] : f[0]).focus();
    });

    // web fonts change every measurement the fit made with the fallback font
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', refit);
    window.addEventListener('resize', refit, { passive: true });
    window.addEventListener('orientationchange', refit, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', refit, { passive: true });
    var panel = host.querySelector('.lbqs-dialog__panel');
    if (panel && window.ResizeObserver) {
      var lastW = 0;
      new ResizeObserver(function (entries) {
        var w = Math.round(entries[0].contentRect.width);
        if (w !== lastW) { lastW = w; refit(); }
      }).observe(panel);
    }

    /* ── 4: one fetch at a time; the latest tap wins ─────────────────── */
    // A second card tapped while the first is loading must not start its own
    // fetch (whichever answered last would fill the dialog). It used to be
    // dropped, so the shopper got the first card. Now it is remembered and
    // loaded the moment the first one has opened the dialog (the opening
    // observer above).
    document.addEventListener('click', function (e) {
      var opener = e.target.closest && e.target.closest('[onclick*="dialogQuickShop.showModal"], .lb-sill');
      if (!opener) return;
      var busy = document.querySelector('.lb-sill[disabled]:not(.lb-sill--out), [onclick*="dialogQuickShop.showModal"][disabled]');
      if (busy && busy !== opener) {
        e.preventDefault();
        e.stopImmediatePropagation();
        queued = opener;
      }
    }, true);

    /* ── 5: the size check ─────────────────────────────────────────── */
    var sizeModal = document.getElementById('sizeModal');
    if (sizeModal) {
      var lastGate = null;
      var gateAt = 0;
      var dialogOpen = function () { return !host.hasAttribute('hidden'); };
      var shut = function () {
        sizeModal.style.display = 'none';
        sizeModal.style.zIndex = '';
        // the dialog is still open: give scrolling back to its own lock
        document.body.style.overflow = dialogOpen() ? '' : 'auto';
      };
      document.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        var gateBtn = e.target.closest(SEL.host + ' ' + SEL.gate);
        if (gateBtn) {
          lastGate = gateBtn.parentNode;
          gateAt = Date.now();
          // the dialog sits at 100000; the size check (theme: 9999) must be above it
          sizeModal.style.zIndex = '100001';
          setTimeout(function () {
            if (sizeModal.style.display === 'block') {
              var c = document.getElementById('confirmSize');
              if (c) c.focus({ preventScroll: true });
            }
          }, 0);
        }
      });
      // capture: runs before the listeners the theme stacked on these buttons
      document.addEventListener('click', function (e) {
        if (!e.target.closest || !lastGate) return;
        var confirm = e.target.closest('#confirmSize');
        var close = e.target.closest('#closeModalBtn');
        if (!confirm && !close) return;
        var gate = lastGate;
        if (!gate.isConnected || !dialogOpen()) { lastGate = null; return; }
        e.preventDefault();
        e.stopImmediatePropagation();
        lastGate = null;
        if (confirm && typeof gate.confirmSizeAndAddToCart === 'function' && gate.submitBtn) {
          gate.confirmSizeAndAddToCart();
          shut();
          var s = gate.submitBtn;
          if (s && s.offsetParent !== null) s.focus({ preventScroll: true });
        } else {
          shut();
          var g = gate.querySelector('button[type="button"]');
          if (g && g.offsetParent !== null) g.focus({ preventScroll: true });
        }
      }, true);
      // A double-click on Add to cart: the first click opens the size check,
      // the second lands on its backdrop a few ms later and closed it again
      // (ours below and the theme's window listener) — the shopper saw a
      // flicker and nothing was added. That second click is not a dismissal.
      document.addEventListener('click', function (e) {
        if (e.target !== sizeModal || sizeModal.style.display !== 'block') return;
        if (e.detail > 1 || Date.now() - gateAt < 500) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
      // a click on the size check's backdrop
      sizeModal.addEventListener('click', function (e) {
        if (e.target === sizeModal && lastGate) { lastGate = null; shut(); }
      });
      // the dialog closes on Escape *keyup*; that keyup belongs to the size check
      var eatKeyup = false;
      document.addEventListener('keyup', function (e) {
        if (eatKeyup && e.key === 'Escape') { eatKeyup = false; e.stopPropagation(); }
      }, true);
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || sizeModal.style.display !== 'block') return;
        e.preventDefault();
        e.stopPropagation();
        eatKeyup = true;
        var g = lastGate;
        lastGate = null;
        shut();
        var b = g && g.querySelector('button[type="button"]');
        if (b && b.offsetParent !== null) b.focus({ preventScroll: true });
      }, true);
    }
  });
})();
